import React, { useState } from 'react'
import { PageHeader, Card, Badge, Button, Input } from '@pilot-hub/ui'

interface MissionDetailPageProps {
  missionId?: string
}

export function MissionDetailPage({ missionId }: MissionDetailPageProps) {
  // Mock mission data
  const mission = {
    id: missionId || '1',
    title: 'Waldbrand Eberstadt',
    type: 'EINSATZ',
    status: 'AKTIV',
    priority: 'HOCH',
    plannedStart: '2024-07-15T10:00:00',
    plannedEnd: '2024-07-15T18:00:00',
    location: 'Eberstadt, Waldgebiet',
    lat: 49.8728,
    lng: 8.6512,
    radius: 2000,
    leaderName: 'Max Mustermann',
    description: 'Großflächiger Waldbrand im Eberstädter Forst. Mehrere Drohnen im Einsatz zur Lageerkundung und Brandüberwachung.',
    riskAssessment: 'Hohe Waldbrandgefahr durch Trockenheit. Wind dreht auf Nordost. Evakuierung von Naherholungsgebieten vorbereitet.',
    drones: [
      { id: 1, inventoryNumber: 'ASB-001', model: 'Matrice 30T' },
      { id: 2, inventoryNumber: 'ASB-002', model: 'Mavic 3 Enterprise' },
    ],
    vehicles: [
      { id: 1, callSign: 'FLORIAN 10/1', type: 'MTF' },
      { id: 2, callSign: 'FLORIAN 10/2', type: 'LF 10' },
    ],
    participants: [
      { id: 1, name: 'Max Mustermann', function: 'Einsatzleiter' },
      { id: 2, name: 'Anna Schmidt', function: 'Pilot' },
      { id: 3, name: 'Hans Müller', function: 'Co-Pilot' },
    ]
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={mission.title}
        subtitle={`${mission.type} • ${mission.status} • ${new Date('2024-07-15T10:00:00').toLocaleString('de-DE')}`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
          <p className="text-xl font-bold"><Badge variant="danger">{mission.status}</Badge></p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Typ</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{mission.type}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Einsatzleiter</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{mission.leaderName || 'Nicht zugewiesen'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Ort</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{mission.location || 'Nicht angegeben'}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="card-header">
            <div className="card-title">Details</div>
          </div>
          <div className="card-content space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Beschreibung</p>
                <p className="text-gray-900 dark:text-white mt-1">{mission.description || 'Keine Beschreibung'}</p>
              </div>
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Risikobewertung</p>
                <p className="text-gray-900 dark:text-white mt-1">{mission.riskAssessment || 'Keine Risikobewertung'}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Geplant Start</p>
                <p className="text-gray-900 dark:text-white font-mono">{new Date('2024-07-15T10:00:00').toLocaleString('de-DE')}</p>
              </div>
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Geplant Ende</p>
                <p className="text-gray-900 dark:text-white font-mono">{mission.plannedEnd ? new Date(mission.plannedEnd).toLocaleString('de-DE') : 'Offen'}</p>
              </div>
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Einsatzgebiet Radius</p>
                <p className="text-gray-900 dark:text-white font-mono">{mission.radius ? `${mission.radius}m` : 'Nicht festgelegt'}</p>
              </div>
            </div>
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Koordinaten</p>
              <p className="text-gray-900 dark:text-white font-mono mt-1">{mission.lat && mission.lng ? `${mission.lat}, ${mission.lng}` : 'Nicht festgelegt'}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="card-header">
            <div className="card-title">Ressourcen</div>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Zugewiesene Drohnen</h4>
                <div className="flex flex-wrap gap-2">
                  {mission.drones?.map(d => (
                    <Badge key={d.id} variant="info">{d.inventoryNumber} - {d.model}</Badge>
                  )) || <span className="text-gray-500 dark:text-gray-400">Keine Drohnen zugewiesen</span>}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Zugewiesene Fahrzeuge</h4>
                <div className="flex flex-wrap gap-2">
                  {mission.vehicles?.map(v => (
                    <Badge key={v.id} variant="secondary">{v.callSign} - {v.type}</Badge>
                  )) || <span className="text-gray-500 dark:text-gray-400">Keine Fahrzeuge zugewiesen</span>}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Teilnehmende Piloten</h4>
                <div className="flex flex-wrap gap-2">
                  {mission.participants?.map(p => (
                    <Badge key={p.id} variant="info">{p.name} ({p.function})</Badge>
                  )) || <span className="text-gray-500 dark:text-gray-400">Keine Piloten zugewiesen</span>}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}